import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material'
import { styled } from '@mui/system'
import { colorPalette } from '../../../../style/colorPalette'
import { Button } from '../../../../components/buttons'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { StoredUiStateFilteringInput } from '../../../../components/dialogs/stored-ui-state-dialog/stored-ui-state-list'
import { FontSize, FontWeight } from '../../../../../styles/commonStyles'
import SvgFilterIcon from '../../../../components/icons/FilterIcon'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { AllState } from '../../../../../store'
import { ScopeIcon } from '../../../../components/dialogs/SavedUIStateDialog/contents/ScopeIcon'
import { UiStateScope } from '../../../../../lib/commons/uiStates'
import { PermanentUiState } from '../../../../../services/model/uiState'
import {
  SearchConditionAndColumnStateDraft,
  StoredSearchConditionAndColumnStateEditorDialog,
} from '../../../../components/dialogs/stored-ui-state-dialog/stored-ui-state-editor-dialog/StoredSearchConditionAndColumnStateEditorDialog'
import { SavePermanentUiStateRequest } from '../../../../../services/storage-services/ui-states/permanentUiStateStorageService'
import {
  SearchConditionAndColumnStateV2,
  SearchConditionAndColumnStateV2StorageService,
  useSearchConditionAndColumnStateV2StorageService,
} from '../../../../../services/storage-services/ui-states/searchConditionAndColumnStateV2StorageService'
import { useProjectPrivateContext } from '../../../../context/projectContext'
import { BulkSheetColumnState } from '../../../../model/bulkSheetColumnAndFilterState'
import { WbsItemSearchConditionV2VO } from '../../../../../domain/value-object/WbsItemSearchConditionV2Vo'
import { intl } from '../../../../../i18n'
import SavedUIStateCopyUrlIconButton from '../../../../components/dialogs/SavedUIStateDialog/contents/SavedUIStateCopyUrlIconButton'
import {
  copyUrl,
  isSharableSavedUIState,
  isSupportedURLCopy,
  SavedUIState,
  UIState,
} from '../../../../components/dialogs/SavedUIStateDialog/SavedUIStateList'
import { SearchConditionAndColumnState } from '../../../../../services/model/searchConditionAndColumnState'
import { WbsItemSearchConditionVO } from '../../../../../domain/value-object/WbsItemSearchConditionVo'

type Props = {
  open: boolean
  handleSavedSearchConditionPanelClose: () => void
  filteringInput: string
  setFilteringInput: (newValue: string) => void
  onSelectState: (
    selected: PermanentUiState<SearchConditionAndColumnStateV2>
  ) => void
  functionUuid: string
  searchCondition: WbsItemSearchConditionV2VO
}

const SaveSearchConditionListDialogV2 = ({
  open,
  handleSavedSearchConditionPanelClose,
  filteringInput,
  setFilteringInput,
  onSelectState,
  functionUuid,
  searchCondition,
}: Props) => {
  const [openCreateDialog, setOpenCreateDialog] = useState<boolean>(false)
  const closeCreateDialog = useCallback(() => {
    setOpenCreateDialog(false)
  }, [])
  const [editorDraft, setEditorDraft] = useState<
    SearchConditionAndColumnStateDraft | undefined
  >(undefined)
  const onCreate = useCallback(() => {
    setOpenCreateDialog(true)
    setEditorDraft({
      type: 'Create',
      searchCondition: searchCondition as unknown as WbsItemSearchConditionVO,
    })
  }, [searchCondition])

  const { projectUuid } = useProjectPrivateContext()
  const storageService = useSearchConditionAndColumnStateV2StorageService(
    functionUuid,
    projectUuid
  )
  useEffect(() => {
    if (open) {
      storageService.fetch()
    }
  }, [open])

  const onSubmit = useCallback(
    (request: SavePermanentUiStateRequest<SearchConditionAndColumnState>) => {
      storageService.save(
        request as unknown as SavePermanentUiStateRequest<SearchConditionAndColumnStateV2>
      )
      setEditorDraft(undefined)
    },
    [storageService.save]
  )
  const onDelete = useCallback(
    (uuid: string) => {
      storageService.delete(uuid)
    },
    [storageService.delete]
  )

  return (
    <Dialog
      open={open}
      onClose={handleSavedSearchConditionPanelClose}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        '& .MuiPaper-root': { maxWidth: '820px', minHeight: '500px' },
      }}
    >
      <DialogTitle sx={{ padding: '25px 24px 0 30px' }}>
        <DialogRow>
          <Typography
            sx={{ color: colorPalette.monotone[10], fontSize: '15px' }}
          >
            {intl.formatMessage({ id: 'storedSearchCondition' })}
          </Typography>
          <Button
            colorPattern="monotone"
            variant="outlined"
            sx={{
              margin: 'auto 20px auto auto',
              cursor: 'pointer',
            }}
            onClick={handleSavedSearchConditionPanelClose}
          >
            <ClearRoundedIcon />
            <Typography>
              {intl.formatMessage({ id: 'dialog.close' })}
            </Typography>
          </Button>
        </DialogRow>
        <Divider sx={{ margin: '20px 0' }} />
        <DialogRow>
          <Typography
            sx={{ color: colorPalette.monotone[10], fontSize: '16px' }}
          >
            {intl.formatMessage({ id: 'savedUIState.content.title.list' })}
          </Typography>
          <Button
            colorPattern="skyBlue"
            variant="outlined"
            sx={{
              marginLeft: '20px',
              cursor: 'pointer',
            }}
            onClick={onCreate}
          >
            <AddRoundedIcon />
            <Typography>
              {intl.formatMessage({ id: 'savedUIState.save' })}
            </Typography>
          </Button>
          <FilteringInputContainer>
            <StoredUiStateFilteringInput
              value={filteringInput}
              onChange={setFilteringInput}
              placeholder={intl.formatMessage({
                id: 'savedUIState.search.condition.name',
              })}
              isSearchIcon={true}
              width={250}
              onClear={() => setFilteringInput('')}
            />
          </FilteringInputContainer>
          <StoredSearchConditionAndColumnStateEditorDialog
            functionUuid={functionUuid}
            open={openCreateDialog}
            draft={editorDraft}
            onClose={closeCreateDialog}
            onSubmit={onSubmit}
            currentColumnState={[]}
            onDelete={onDelete}
          />
        </DialogRow>
        <ListHeaderRow>
          <List
            sx={{
              display: 'flex',
              flexDirection: 'row',
              color: colorPalette.monotone[5],
              fontSize: FontSize.MEDIUM,
              fontWeight: FontWeight.NORMAL,
              width: '100%',
            }}
          >
            <ListItem
              sx={{
                width: '200px',
                padding: 0,
              }}
            >
              <Typography>
                {intl.formatMessage({
                  id: 'savedUIState.saved.search.condition.name',
                })}
              </Typography>
            </ListItem>
            <ListItem
              sx={{
                width: '90px',
                padding: 0,
              }}
            >
              <SvgFilterIcon
                style={{
                  fontSize: 21,
                  paddingRight: '8px',
                  width: 'fit-contnt',
                }}
              />
              <Typography>
                {intl.formatMessage({ id: 'savedUIState.content.list.scope' })}
              </Typography>
            </ListItem>
            <ListItem
              sx={{
                width: '150px',
                padding: 0,
              }}
            >
              <SvgFilterIcon
                style={{
                  fontSize: 21,
                  paddingRight: '8px',
                }}
              />
              <Typography>
                {intl.formatMessage({
                  id: 'savedUIState.content.list.updatedBy',
                })}
              </Typography>
            </ListItem>
            <ListItem
              sx={{
                width: '100px',
                padding: 0,
              }}
            >
              <Typography>
                {intl.formatMessage({
                  id: 'savedUIState.content.list.updatedAt',
                })}
              </Typography>
            </ListItem>
          </List>
        </ListHeaderRow>
      </DialogTitle>
      <DialogContent
        sx={{ padding: '20px 24px 20px 30px', maxHeight: '400px' }}
      >
        <SaveSearchConditionListV2
          onSelect={onSelectState}
          functionUuid={functionUuid}
          currentSearchCondition={searchCondition}
          filteringInput={filteringInput}
          currentColumnState={[]}
          storageService={storageService}
        />
      </DialogContent>
    </Dialog>
  )
}

type SaveSearchConditionListV2Props = {
  onSelect: (
    selected: PermanentUiState<SearchConditionAndColumnStateV2>
  ) => void
  functionUuid: string
  currentSearchCondition: WbsItemSearchConditionV2VO
  currentColumnState: BulkSheetColumnState
  filteringInput: string
  storageService: SearchConditionAndColumnStateV2StorageService
}

const SaveSearchConditionListV2 = ({
  onSelect,
  functionUuid,
  currentSearchCondition,
  currentColumnState,
  filteringInput,
  storageService,
}: SaveSearchConditionListV2Props) => {
  const { projectUuid } = useProjectPrivateContext()

  const { projectIconUrl, tenantIconUrl } = useSelector<
    AllState,
    {
      projectUuid: string | undefined
      projectIconUrl: string | undefined
      tenantIconUrl: string | undefined
      hasAllPrivileges: boolean | undefined
    }
  >(state => ({
    projectUuid: state.project.selected,
    projectIconUrl: state.project.current?.iconUrl,
    tenantIconUrl: state.tenant.organization?.iconUrl,
    hasAllPrivileges: state.user.user?.hasAllPrivileges,
  }))

  const scopeIcon = useCallback(
    (scope: 'CROSS_PROJECT' | 'TENANT' | 'USER') => {
      return scope ? <ScopeIcon scope={scope as UiStateScope} /> : <></>
    },
    [projectUuid, projectIconUrl, tenantIconUrl]
  )

  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false)

  const handleEditDialogClose = () => {
    setOpenEditDialog(false)
  }

  const [editorDraft, setEditorDraft] = useState<
    SearchConditionAndColumnStateDraft | undefined
  >(undefined)

  const onUpdate = useCallback(
    (target: PermanentUiState<SearchConditionAndColumnStateV2>) => {
      setOpenEditDialog(true)
      setEditorDraft({
        type: 'Update',
        uuid: target.uuid,
        name: target.name,
        scope: target.scope,
        searchCondition:
          currentSearchCondition as unknown as WbsItemSearchConditionVO,
      })
    },
    [currentSearchCondition]
  )
  const onDelete = useCallback(
    (uuid: string) => {
      storageService.delete(uuid)
    },
    [storageService.delete]
  )
  const onSubmit = useCallback(
    (request: SavePermanentUiStateRequest<SearchConditionAndColumnState>) => {
      storageService.save(
        request as unknown as SavePermanentUiStateRequest<SearchConditionAndColumnStateV2>
      )
      setEditorDraft(undefined)
    },
    [storageService.save]
  )

  const filteredStates = useMemo(
    () => storageService.stored.filter(s => s.name.includes(filteringInput)),
    [storageService, filteringInput]
  )

  const isUiStateScope = useCallback(
    (strScope: string): strScope is UiStateScope => {
      return Object.values(UiStateScope).includes(strScope as UiStateScope)
    },
    []
  )

  const permanentUiStateToSavedUIState = useCallback(
    (permanentUiState: PermanentUiState<UIState>): SavedUIState => {
      return {
        uuid: permanentUiState.uuid,
        code: permanentUiState.code,
        name: permanentUiState.name,
        UIState: permanentUiState.value,
        scope: isUiStateScope(permanentUiState.scope)
          ? permanentUiState.scope
          : UiStateScope.User,
        updatedBy: permanentUiState.updatedBy,
        updatedAt: permanentUiState.updatedAt,
      }
    },
    [isUiStateScope]
  )

  const isSharablePermanentUiState = useCallback(
    (storedState: PermanentUiState<SearchConditionAndColumnStateV2>) =>
      isSharableSavedUIState(permanentUiStateToSavedUIState(storedState)),
    [permanentUiStateToSavedUIState]
  )

  const copyUrlByPermanentUiState = useCallback(
    (storedState: PermanentUiState<SearchConditionAndColumnStateV2>) => {
      copyUrl(permanentUiStateToSavedUIState(storedState))
    },
    [permanentUiStateToSavedUIState]
  )

  return (
    <List>
      {filteredStates.map(storedState => (
        <ListItem key={storedState.uuid} sx={{ margin: '10px 0 ', padding: 0 }}>
          <ListItem sx={{ maxWidth: '200px', padding: 0 }}>
            <ListItemText
              sx={{
                color: colorPalette.monotone[10],
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                overflow: 'hidden',
                fontWeight: 500,
              }}
            >
              {storedState.name}
            </ListItemText>
          </ListItem>
          <ListItem
            sx={{
              maxWidth: '90px',
              display: 'flex',
              justifyContent: 'center',
              padding: 0,
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: '64px',
                display: 'flex',
                alignContent: 'center',
                justifyContent: 'start',
              }}
            >
              {scopeIcon(storedState.scope)}
            </ListItemIcon>
          </ListItem>
          <ListItem sx={{ maxWidth: '150px', padding: 0 }}>
            <ListItemText
              sx={{ fontWeight: 500, color: colorPalette.monotone[10] }}
            >
              {storedState.updatedBy}
            </ListItemText>
          </ListItem>
          <ListItem
            sx={{
              maxWidth: isSharablePermanentUiState(storedState)
                ? '168px'
                : undefined,
              padding: '0 20px 0 0',
            }}
          >
            <ListItemText
              sx={{ color: colorPalette.monotone[4], fontWeight: 400 }}
            >
              {storedState.updatedAt}
            </ListItemText>
          </ListItem>
          {isSharablePermanentUiState(storedState) && (
            <ListItem
              sx={{ maxWidth: '40px', paddingRight: '10px', padding: 0 }}
            >
              <SavedUIStateCopyUrlIconButton
                isSupportedURLCopy={isSupportedURLCopy}
                onClickUrlIcon={() => copyUrlByPermanentUiState(storedState)}
              />
            </ListItem>
          )}
          <ListItem sx={{ maxWidth: '60px', paddingRight: '10px', padding: 0 }}>
            <Button
              colorPattern="monotone"
              variant="outlined"
              onClick={() => onUpdate(storedState)}
              sx={{ cursor: 'pointer' }}
            >
              <Typography>
                {intl.formatMessage({ id: 'savedUIState.content.button.edit' })}
              </Typography>
            </Button>
          </ListItem>
          <ListItem sx={{ maxWidth: '60px', paddingRight: '10px', padding: 0 }}>
            <Button
              colorPattern="skyBlue"
              variant="filled"
              sx={{ cursor: 'pointer' }}
              onClick={() => onSelect(storedState)}
            >
              <Typography>
                {intl.formatMessage({
                  id: 'savedUIState.content.button.apply',
                })}
              </Typography>
            </Button>
          </ListItem>
        </ListItem>
      ))}
      <StoredSearchConditionAndColumnStateEditorDialog
        functionUuid={functionUuid}
        open={openEditDialog}
        draft={editorDraft}
        onClose={handleEditDialogClose}
        onSubmit={onSubmit}
        currentColumnState={currentColumnState}
        onDelete={onDelete}
      />
    </List>
  )
}

const DialogRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '770px',
})

const FilteringInputContainer = styled('div')({
  display: 'flex',
  alignItems: 'center',
  height: '30px',
  margin: 'auto 20px auto auto',
})

const ListHeaderRow = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  width: '770px',
  marginTop: '20px',
})

export default SaveSearchConditionListDialogV2
